import { useState, useEffect, useRef } from "react";
import "@/App.css";
import "./styles/modern.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, MapPin, Phone, Mail, Send, MessageCircle, 
  ChevronDown, Apple, PlayCircle, Award, Users, Target, Star, Facebook, User, LogIn, LogOut, ExternalLink
} from "lucide-react";
import { FaApple, FaGooglePlay, FaFacebookF, FaInstagram, FaTiktok } from "react-icons/fa";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Toaster, toast } from "sonner";
import Admin from "./Admin";
import Shop from "./Shop";
import ShopAdmin from "./ShopAdmin";
import OrderTracking from "./OrderTracking";
import Competition from "./Competition";
import LiveDisplay from "./LiveDisplay";
import Tag from "./Tag";
import GepardKassza from "./GepardKassza";
import KasszaKosar from "./KasszaKosar";
import { PaymentSuccess, PaymentCancel } from "./PaymentResult";
import ResultApproval from "./ResultApproval";
import Akademia from "./Akademia";
import Hianyzas from "./Hianyzas";
import Ugyfelkapu from "./Ugyfelkapu";
import FiokHitelesites from "./FiokHitelesites";
import { AuthModal } from "./components/AuthModal";
import { ProfileMenu } from "./components/ProfileMenu";
// Popup disabled - no announcement needed
// import AnnouncementPopup from "./components/AnnouncementPopup";
// Maintenance mode disabled - all pages accessible
// import UnderDevelopmentPage, { isMaintenanceMode, ALLOWED_PATHS } from "./components/UnderDevelopmentPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL (white outline on transparent)
const LOGO_URL = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/0lyoa6pm_No%20hatter%2C%20feher.png";

// Loading Screen Logo (black background with white drawing)
const LOADING_LOGO_URL = "https://customer-assets-eiarnc6j.emergentagent.net/job_equestrian-hub-20/artifacts/vrc3omri_IMG_0569.jpeg";

// Loading Screen Component
const LoadingScreen = ({ isLoading }) => {
  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          data-testid="loading-screen"
        >
          <motion.div
            className="flex flex-col items-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.img
              src={LOADING_LOGO_URL}
              alt="Gepárd Lovas Egyesület"
              className="w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 object-contain rounded-lg"
              animate={{
                scale: [1, 1.02, 1],
                opacity: [1, 0.8, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="mt-8 flex gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <motion.div
                className="w-2 h-2 bg-[#D4AF37] rounded-full"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
              />
              <motion.div
                className="w-2 h-2 bg-[#D4AF37] rounded-full"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: 0.15 }}
              />
              <motion.div
                className="w-2 h-2 bg-[#D4AF37] rounded-full"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 0.6, repeat: Infinity, delay: 0.3 }}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// /tag: only the invitation setup flow remains here; everything else lives in /ugyfelkapu
const TagRedirect = () => {
  const { search } = useLocation();
  return new URLSearchParams(search).get("setup") ? <Tag /> : <Navigate to="/ugyfelkapu" replace />;
};

// Hero background
const HERO_BG = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/5kzn2031_IMG_0255.jpeg";

// Auth components imported from ./components/AuthModal

// Navigation component
const Navbar = ({ scrolled, user, onLoginClick, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navLinks = [
    { href: "#home", label: "Főoldal" },
    { href: "#about", label: "Rólunk" },
    { href: "#services", label: "Szolgáltatások" },
    { href: "#gallery", label: "Galéria" },
    { href: "#prices", label: "Árak" },
    { href: "#contact", label: "Kapcsolat" }
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'navbar-glass py-3' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <a href="#home" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="Gepárd Lovas Egyesület" className="h-10 w-auto" />
            </a>
            
            {/* Desktop menu */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-white/80 hover:text-[#D4AF37] transition-colors duration-200"
                  data-testid={`nav-${link.href.substring(1)}`}
                >
                  {link.label}
                </a>
              ))}
              {!user && (
                <button
                  onClick={onLoginClick}
                  className="flex items-center gap-2 text-sm font-medium text-[#D4AF37] hover:text-[#F3E5AB] transition-colors duration-200"
                  data-testid="nav-login"
                >
                  <LogIn size={18} />
                  Bejelentkezés
                </button>
              )}
              {user && <ProfileMenu user={user} onLogout={onLogout} />}
            </div>
            
            {/* Mobile: profile + menu button */}
            <div className="md:hidden flex items-center gap-2">
              {user && <ProfileMenu user={user} onLogout={onLogout} />}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="text-white p-2"
                data-testid="mobile-menu-button"
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-menu"
          >
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-6 right-6 text-white p-2"
              data-testid="mobile-menu-close"
            >
              <X size={24} />
            </button>
            {navLinks.map((link, index) => (
              <motion.a
                key={link.href}
                href={link.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setMobileMenuOpen(false)}
                className="text-2xl font-semibold text-white hover:text-[#D4AF37] transition-colors"
                data-testid={`mobile-nav-${link.href.substring(1)}`}
              >
                {link.label}
              </motion.a>
            ))}
            {!user && (
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: navLinks.length * 0.1 }}
                onClick={() => { setMobileMenuOpen(false); onLoginClick(); }}
                className="text-2xl font-semibold text-[#D4AF37] hover:text-[#F3E5AB] transition-colors flex items-center gap-3"
                data-testid="mobile-nav-login"
              >
                <LogIn size={24} />
                Bejelentkezés
              </motion.button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="hero-section">
      <div 
        className="hero-background"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="hero-overlay" />
      
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <motion.img
          src={LOGO_URL}
          alt="Gepárd Lovas Egyesület"
          className="w-32 sm:w-40 md:w-48 mx-auto mb-8"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        />
        <motion.h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Gepárd Lovas Egyesület
        </motion.h1>
        <motion.p
          className="text-lg sm:text-xl text-white/80 mb-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Lovaglás Orosházán – kezdőtől versenyzőig
        </motion.p>
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <a href="#services" className="btn-gold px-8 py-3 rounded-full font-medium inline-flex items-center justify-center gap-2" data-testid="hero-cta-services">
            Szolgáltatásaink
          </a>
          <a href="#contact" className="btn-gold-outline px-8 py-3 rounded-full font-medium inline-flex items-center justify-center gap-2" data-testid="hero-cta-contact">
            Kapcsolat
          </a>
        </motion.div>
        
        <motion.div
          className="mt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
        >
          <a href="#app" className="text-white/60 hover:text-[#D4AF37] transition-colors">
            <ChevronDown size={32} className="mx-auto animate-bounce" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// App Promo Section
const AppSection = () => {
  const features = [
    "Edzések nyomon követése",
    "Kihívások és pontgyűjtés",
    "Értékelések megtekintése",
    "Események kezelése",
    "Fontos információk"
  ];

  return (
    <section id="app" className="py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-[#141414] border border-[rgba(212,175,55,0.3)] rounded-lg p-8 md:p-12"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D4AF37] text-sm font-medium uppercase tracking-wider">Hamarosan</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2 mb-6">
                Hivatalos Alkalmazás
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                A Gepárd Lovas Egyesület hivatalos alkalmazása hamarosan elérhető, ahol minden egy helyen kezelhető.
              </p>
              
              <ul className="space-y-4 mb-10">
                {features.map((feature, index) => (
                  <motion.li
                    key={index}
                    className="flex items-center gap-3 text-white/80"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                    {feature}
                  </motion.li>
                ))}
              </ul>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="btn-gold px-6 py-3 rounded-lg font-medium inline-flex items-center justify-center gap-3 opacity-70 cursor-not-allowed" disabled data-testid="app-store-btn">
                  <FaApple size={24} />
                  <div className="text-left">
                    <div className="text-xs">Hamarosan</div>
                    <div className="text-sm font-semibold">App Store</div>
                  </div>
                </button>
                <button className="btn-gold px-6 py-3 rounded-lg font-medium inline-flex items-center justify-center gap-3 opacity-70 cursor-not-allowed" disabled data-testid="google-play-btn">
                  <FaGooglePlay size={20} />
                  <div className="text-left">
                    <div className="text-xs">Hamarosan</div>
                    <div className="text-sm font-semibold">Google Play</div>
                  </div>
                </button>
              </div>
            </div>
            
            <div className="hidden md:flex justify-center items-center">
              <div className="relative">
                <div className="w-64 h-[500px] bg-[#0A0A0A] rounded-[3rem] border-4 border-[#D4AF37]/30 flex items-center justify-center">
                  <img src={LOGO_URL} alt="App preview" className="w-32 opacity-50" />
                </div>
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#D4AF37]/20 rounded-full blur-xl" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-2xl" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 bg-[#FAFAFA]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-[#18181B] mb-8">
            Bemutatkozás
          </h2>
          <div className="space-y-6 text-[#71717A] text-lg leading-relaxed">
            <p>
              Sziasztok! Hegedűs Titanilla vagyok, a Szent István Egyetemen végeztem először vidékfejlesztő agrármérnökként, majd az Állatorvostudományi Egyetem másoddiplomás képzésén hippológus (lovastanári) diplomát szereztem. 16 éve díjugratok, jelenleg saját képzésű lovammal 125 cm-en versenyzek.
            </p>
            <p>
              Fő célom a lóközpontú lovaglás oktatása, fiatal lovasok versenyeztetése és a minőségi lovasoktatás. Továbbá foglalkozunk csoportos lovaskocsikáztatással, falunapokon, gyereknapokon lovagoltatással, tereplovaglással és lovastáboroztatással.
            </p>
            <p>
              Lovardánk a városközponttól 3 km-re, még Orosházán belül helyezkedik el, jól megközelíthető főút mellett. Lovaink saját képzésűek, fiatalok, teljesen megbízhatóak. Jelenleg sok saját képzésű lovunk is van, a mini shetlanditól az óriásig, mindenki megtalálja a számára megfelelő méretű lovat.
            </p>
            <p>
              Foglalkozunk kisiskolás korú gyerekektől a felnőttekig mindenkivel. Lehetőség van nálunk hobby szintű lovaglásra, díjugratásra, díjlovaglásra és versenyzésre is.
            </p>
          </div>
          
          <div className="grid grid-cols-3 gap-8 mt-12">
            {[
              { label: "Elégedett lovas", value: "100+" },
              { label: "Képzett ló", value: "30+" },
              { label: "Oktató", value: "1" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-3xl sm:text-4xl font-bold text-[#D4AF37]">{stat.value}</div>
                <div className="text-sm text-[#71717A] mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const serviceGroups = [
    {
      title: "Oktatás",
      items: ["Alapszintű lovasoktatás", "Versenyzők képzése", "Elméleti oktatások", "Osztálylovaglások"]
    },
    {
      title: "Versenysportok",
      items: ["Díjugrató", "Díjlovagló", "Versenyeztetés", "Edzőtáborok"]
    },
    {
      title: "Egyéni programok",
      items: ["Idomító egyéni edzések", "Szabadidomítás", "Lovak belovaglása, képzése", "Elrontott lovak idomítása"]
    },
    {
      title: "Élmények",
      items: ["Tereplovaglási lehetőségek", "Terepkocsikázás", "Csoportos lovaskocsizás", "Fogathajtás"]
    },
    {
      title: "Táborok & Rendezvények",
      items: ["Lovastáborok", "Sétalovagoltatás", "Falunap, gyereknap"]
    }
  ];

  return (
    <section id="services" className="py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Szolgáltatásaink
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Széles körű lovas programok kezdőktől a versenyzőkig
          </p>
        </motion.div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {serviceGroups.map((group, index) => (
            <motion.div
              key={index}
              className="card-dark p-6 rounded-lg"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`service-card-${index}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                  {index === 0 && <Users className="w-5 h-5 text-[#D4AF37]" />}
                  {index === 1 && <Award className="w-5 h-5 text-[#D4AF37]" />}
                  {index === 2 && <Target className="w-5 h-5 text-[#D4AF37]" />}
                  {index === 3 && <Star className="w-5 h-5 text-[#D4AF37]" />}
                  {index === 4 && <Users className="w-5 h-5 text-[#D4AF37]" />}
                </div>
                <h3 className="text-xl font-semibold text-white">
                  {group.title}
                </h3>
              </div>
              <ul className="space-y-2">
                {group.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center gap-2 text-white/70 text-sm">
                    <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = ({ galleryImages }) => {
  if (!galleryImages || galleryImages.length === 0) {
    return null;
  }

  return (
    <section id="gallery" className="py-24 bg-[#141414]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Galéria
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Pillanatok a lovardából
          </p>
        </motion.div>
        
        <div className="gallery-grid">
          {galleryImages.map((image, index) => (
            <motion.div
              key={index}
              className={`gallery-item ${index === 0 ? 'aspect-square md:aspect-auto' : 'aspect-square'}`}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`gallery-item-${index}`}
            >
              <img
                src={image.url}
                alt={image.alt || 'Galéria kép'}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const prices = [
    { name: "Osztálylovaglás 1 óra", price: "3.500 Ft" },
    { name: "Futószár 30 perc", price: "3.000 Ft" },
    { name: "Ugró 1 óra 30 perc", price: "4.500 Ft" },
    { name: "Póni óvodás 15 perc", price: "1.500 Ft" },
    { name: "Póni óvodás 20 perc", price: "2.000 Ft" },
    { name: "Terep kezdő 2 óra", price: "5.500 Ft" },
    { name: "Terep haladó 3 óra", price: "6.500 Ft" },
    { name: "Egyéni edzés sík 35 perc", price: "5.000 Ft" },
    { name: "Egyéni edzés ugró 1 óra 30 perc", price: "6.000 Ft" }
  ];

  return (
    <section id="prices" className="py-24 bg-[#FAFAFA]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-[#18181B] mb-4">
            Árak
          </h2>
          <p className="text-[#71717A]">
            Átlátható árazás, nincsenek rejtett költségek
          </p>
        </motion.div>
        
        <motion.div
          className="bg-white rounded-lg shadow-lg p-6 md:p-8"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          {prices.map((item, index) => (
            <div key={index} className="price-item" data-testid={`price-item-${index}`}>
              <div className="font-medium text-[#18181B]">{item.name}</div>
              <div className="text-xl font-bold text-[#D4AF37]">{item.price}</div>
            </div>
          ))}
        </motion.div>
        
        <p className="text-center text-sm text-[#71717A] mt-6">
          Kérdésed van? Hívj minket: <a href="tel:+36306244757" className="text-[#D4AF37] hover:underline">+36 30 624 4757</a>
        </p>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Kapcsolat
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Keress minket bátran!
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-8">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Cím</h3>
                <p className="text-white/60">5900 Orosháza, Földvári út 37.</p>
                <p className="text-white/40 text-sm">3 km a városközponttól</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Phone className="text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Telefon</h3>
                <a href="tel:+36306244757" className="text-white/60 hover:text-[#D4AF37] transition-colors">+36 30 624 4757</a>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Email</h3>
                <a href="mailto:gepardlovasegyesulet@gmail.com" className="text-white/60 hover:text-[#D4AF37] transition-colors">gepardlovasegyesulet@gmail.com</a>
              </div>
            </div>
            
            <a
              href="https://www.facebook.com/gepardlovasklub"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 group"
              data-testid="facebook-link"
            >
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/20 transition-colors">
                <FaFacebookF className="text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Facebook</h3>
                <p className="text-white/60 group-hover:text-[#D4AF37] transition-colors">Gepárd Lovas Egyesület</p>
              </div>
            </a>
            
            <a
              href="https://www.instagram.com/gepardle?igsh=Z2Y1OXljZWhwbTF0"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 group"
              data-testid="instagram-link"
            >
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/20 transition-colors">
                <FaInstagram className="text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">Instagram</h3>
                <p className="text-white/60 group-hover:text-[#D4AF37] transition-colors">@gepardle</p>
              </div>
            </a>
            
            <a
              href="https://www.tiktok.com/@gepardle?_r=1&_t=ZN-94vVw3Tmdxj"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 group"
              data-testid="tiktok-link"
            >
              <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/20 transition-colors">
                <FaTiktok className="text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-semibold text-white mb-1">TikTok</h3>
                <p className="text-white/60 group-hover:text-[#D4AF37] transition-colors">@gepardle</p>
              </div>
            </a>
          </motion.div>
          
          <motion.div
            className="h-80 md:h-auto rounded-lg overflow-hidden map-container"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2738.5!2d20.6445!3d46.5680!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4744809c2a3b7f5d%3A0x7c8d9e0f1a2b3c4d!2zRsO2bGR2w6FyaSDDunQgMzcsIDU5MDAgT3Jvc2jDoXph!5e0!3m2!1shu!2shu!4v1699999999999!5m2!1shu!2shu"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '300px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Gepárd Lovas Egyesület helyszín"
              data-testid="google-map"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (response.data.success) {
        toast.success(response.data.message);
        setEmail("");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Hiba történt. Kérjük próbáld újra később.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 bg-[#0A0A0A] border-t border-[rgba(255,255,255,0.1)]">
      <div className="max-w-xl mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Iratkozz fel hírlevelünkre!
          </h2>
          <p className="text-white/60 mb-8">
            Értesülj elsőként eseményeinkről és híreinkről.
          </p>
          
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              placeholder="Email címed"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-[#141414] border-[rgba(212,175,55,0.3)] text-white placeholder:text-white/40 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
              required
              data-testid="newsletter-email-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="btn-gold px-8 py-2 rounded-md font-medium"
              data-testid="newsletter-submit-btn"
            >
              {loading ? "..." : "Feliratkozás"}
            </Button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-8 bg-[#0A0A0A] border-t border-[rgba(255,255,255,0.05)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Gepárd Lovas Egyesület" className="h-8" />
            <span className="text-white/60 text-sm">© 2021 Gepárd Lovas Egyesület</span>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="https://www.facebook.com/gepardlovasklub"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-[#D4AF37] transition-colors"
              data-testid="footer-facebook-link"
            >
              <FaFacebookF size={18} />
            </a>
            <a
              href="https://www.instagram.com/gepardle?igsh=Z2Y1OXljZWhwbTF0"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-[#D4AF37] transition-colors"
              data-testid="footer-instagram-link"
            >
              <FaInstagram size={18} />
            </a>
            <a
              href="https://www.tiktok.com/@gepardle?_r=1&_t=ZN-94vVw3Tmdxj"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 hover:text-[#D4AF37] transition-colors"
              data-testid="footer-tiktok-link"
            >
              <FaTiktok size={18} />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Chat Widget
const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: "Üdvözöllek! Miben segíthetek a Gepárd Lovas Egyesülettel kapcsolatban?"
      }]);
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage
      });
      
      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Sajnos hiba történt. Kérlek próbáld újra később, vagy vedd fel velünk a kapcsolatot közvetlenül." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chat-widget">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="chat-window"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <div className="chat-header">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="" className="w-8 h-8" />
                <div>
                  <div className="font-semibold text-white text-sm">Gepárd Asszisztens</div>
                  <div className="text-xs text-white/60">Kérdezz bátran!</div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/60 hover:text-white transition-colors"
                data-testid="chat-close-btn"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="chat-messages">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`chat-message ${msg.role}`}
                  data-testid={`chat-message-${index}`}
                >
                  {msg.content}
                </div>
              ))}
              {loading && (
                <div className="chat-message assistant">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <span className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            <form onSubmit={sendMessage} className="chat-input-area">
              <Input
                type="text"
                placeholder="Írd be kérdésed..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="flex-1 bg-[#0A0A0A] border-[rgba(212,175,55,0.3)] text-white placeholder:text-white/40 text-sm"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                type="submit"
                disabled={loading || !input.trim()}
                className="btn-gold p-2 rounded-md"
                data-testid="chat-send-btn"
              >
                <Send size={18} />
              </Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      
      <motion.button
        className="chat-bubble"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="chat-toggle-btn"
      >
        {isOpen ? <X size={24} className="text-[#0A0A0A]" /> : <MessageCircle size={24} className="text-[#0A0A0A]" />}
      </motion.button>
    </div>
  );
};

// Main App
function App() {
  const [scrolled, setScrolled] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initial loading effect
  useEffect(() => {
    // Minimum loading time of 1.5 seconds for UX, then wait for initial data
    const minLoadTime = new Promise(resolve => setTimeout(resolve, 1500));
    
    const loadInitialData = async () => {
      try {
        // Wait for minimum load time
        await minLoadTime;
        // Small additional delay for smooth transition
        await new Promise(resolve => setTimeout(resolve, 200));
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInitialData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Check for existing login
    const storedUser = localStorage.getItem("comp_user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem("comp_user");
      }
    }
  }, []);

  useEffect(() => {
    // Fetch gallery images
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        // Convert relative URLs to full URLs
        const images = response.data.map(img => ({
          ...img,
          url: img.url.startsWith("/api/uploads/") ? `${BACKEND_URL}${img.url}` : img.url
        }));
        setGalleryImages(images);
      } catch (err) {
        console.error("Failed to fetch gallery");
      }
    };
    fetchGallery();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("comp_token");
    localStorage.removeItem("comp_user");
    localStorage.removeItem("comp_admin");
    setUser(null);
    toast.success("Sikeres kijelentkezés!");
  };

  return (
    <BrowserRouter>
      {/* Popup disabled */}
      
      <LoadingScreen isLoading={isLoading} />
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="/admin" element={<Admin />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop-admin" element={<ShopAdmin />} />
        <Route path="/order-tracking/:orderNumber" element={<OrderTracking />} />
        <Route path="/verseny" element={<Competition />} />
        <Route path="/tag" element={<TagRedirect />} />
        <Route path="/verseny/:competitionId" element={<Competition />} />
        <Route path="/verseny/:competitionId/:eventId" element={<Competition />} />
        <Route path="/aktualisverseny" element={<LiveDisplay />} />
        <Route path="/gepardkassza" element={<GepardKassza />} />
        <Route path="/kassza" element={<GepardKassza />} />
        <Route path="/kasszakosar" element={<KasszaKosar />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/shop/order-success" element={<PaymentSuccess />} />
        <Route path="/eredmeny-jovahagyas" element={<ResultApproval />} />
        <Route path="/akademia" element={<Akademia />} />
        <Route path="/hianyzas" element={<Hianyzas />} />
        <Route path="/ügyfelkapu" element={<Ugyfelkapu />} />
        <Route path="/ugyfelkapu" element={<Ugyfelkapu />} />
        <Route path="/fiok-hitelesites" element={<FiokHitelesites />} />
        <Route path="*" element={
          <div className="min-h-screen bg-[#0A0A0A]">
            <Navbar 
              scrolled={scrolled} 
              user={user}
              onLoginClick={() => setShowAuthModal(true)}
              onLogout={handleLogout}
            />
            <main>
              <HeroSection />
              <AboutSection />
              <ServicesSection />
              <GallerySection galleryImages={galleryImages} />
              <PricingSection />
              <ContactSection />
              <NewsletterSection />
            </main>
            <Footer />
            <ChatWidget />
            
            <AuthModal 
              show={showAuthModal}
              onClose={() => setShowAuthModal(false)}
              onLoginSuccess={(userData) => setUser(userData)}
            />
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
